import AuthAPI from '../api/auth';
import BaseActionCableConnector from '../../shared/helpers/BaseActionCableConnector';
import DashboardAudioNotificationHelper from './AudioAlerts/DashboardAudioNotificationHelper';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { emitter } from 'shared/helpers/mitt';
import { useImpersonation } from 'dashboard/composables/useImpersonation';
import { useCallsStore } from 'dashboard/stores/calls';
import {
  applyOutboundAnswer,
  armOutboundRecorder,
  handleWhatsappRemoteEnd,
  isLocalWhatsappCall,
} from 'dashboard/composables/useWhatsappCallSession';
import { VOICE_CALL_PROVIDERS } from 'dashboard/helper/inbox';
import { VOICE_CALL_DIRECTION } from 'dashboard/components-next/message/constants';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';

const { isImpersonating } = useImpersonation();
const UNREAD_COUNTS_REFETCH_THROTTLE_MS = 5000;
// SiteUp: recontagem dos chips a partir de `message.created` e dos eventos de
// conversa (criada, atualizada, status, responsável). Precisa de throttle
// próprio (e mais folgado que o de unread) porque são os eventos mais frequentes
// do sistema: cada recontagem dispara o meta e o meta_bundle (uma contagem por
// chip), e sem throttle o `conversation.updated` de uma conta movimentada
// recontava a cada poucos segundos por aba aberta. Ver onMessageCreated.
const STATS_REFETCH_THROTTLE_MS = 8000;

// Phase 2.8: labels semanticos quando secondary_actor nao tem sender
// (ex: sla_missed_first_response) ou quando o payload veio sem nome do sender.
// Usado como fallback no title do toast.
const TYPE_TITLE_FALLBACK = {
  conversation_creation: 'Nova conversa',
  conversation_assignment: 'Conversa atribuída',
  assigned_conversation_new_message: 'Nova mensagem',
  participating_conversation_new_message: 'Nova mensagem',
  conversation_mention: 'Você foi mencionado',
  sla_missed_first_response: 'SLA: primeira resposta',
  sla_missed_next_response: 'SLA: próxima resposta',
  sla_missed_resolution: 'SLA: resolução',
  captain_handoff: 'Handoff da IA',
  captain_follow_up_due: 'Follow-up da IA',
  captain_runner_failure: 'Falha da IA',
  captain_paused: 'IA pausada',
  captain_scheduling_failed: 'Agendamento da IA',
  captain_pending_auto_resolved: 'IA resolveu',
  kanban_item_assignment: 'Kanban atribuído',
  kanban_item_unassignment: 'Kanban removido',
  kanban_checklist_item_assignment: 'Checklist atribuído',
  kanban_checklist_item_unassignment: 'Checklist removido',
  task_assigned: 'Tarefa atribuída',
  task_status_changed: 'Tarefa atualizada',
  task_dependency_resolved: 'Tarefa desbloqueada',
  task_mention: 'Menção em tarefa',
  voip_hot_lead: 'Lead quente',
  voip_sale_closed: 'Venda fechada',
  voip_negative_sentiment: 'Sentimento negativo',
  voip_critical_keyword: 'Palavra crítica',
  voip_missed_hot_call: 'Chamada perdida',
  voip_appointment_reminder: 'Lembrete de agendamento',
  voip_sla_violation: 'SLA da chamada',
  lead_capture_form: 'Lead do formulário',
};

class ActionCableConnector extends BaseActionCableConnector {
  constructor(app, pubsubToken) {
    const { websocketURL = '' } = window.chatwootConfig || {};
    super(app, pubsubToken, websocketURL);
    this.CancelTyping = [];
    this.lastUnreadCountsFetchAt = null;
    this.unreadCountsFetchTimer = null;
    this.lastStatsFetchAt = null;
    this.statsFetchTimer = null;
    this.events = {
      'message.created': this.onMessageCreated,
      'message.updated': this.onMessageUpdated,
      'conversation.created': this.onConversationCreated,
      'conversation.status_changed': this.onStatusChange,
      'user:logout': this.onLogout,
      'page:reload': this.onReload,
      'assignee.changed': this.onAssigneeChanged,
      'conversation.typing_on': this.onTypingOn,
      'conversation.typing_off': this.onTypingOff,
      'conversation.contact_changed': this.onConversationContactChange,
      'presence.update': this.onPresenceUpdate,
      'contact.deleted': this.onContactDelete,
      'contact.updated': this.onContactUpdate,
      'conversation.mentioned': this.onConversationMentioned,
      'notification.created': this.onNotificationCreated,
      'notification.deleted': this.onNotificationDeleted,
      'notification.updated': this.onNotificationUpdated,
      'conversation.read': this.onConversationRead,
      'conversation.updated': this.onConversationUpdated,
      'conversation.unread_count_changed':
        this.onConversationUnreadCountChanged,
      'account.cache_invalidated': this.onCacheInvalidate,
      'account.enrichment_completed': this.onEnrichmentCompleted,
      'copilot.message.created': this.onCopilotMessageCreated,
      'captain.processing': this.onCaptainProcessing,
      'kanban_item.access_changed': this.onKanbanItemAccessChanged,
      'google_calendar.access_changed': this.onGoogleCalendarAccessChanged,
      'kanban_item.created': this.onKanbanItemUpsert,
      'kanban.card_changed': this.onKanbanCardChanged,
      'kanban.cards_invalidated': this.onKanbanCardsInvalidated,
      'ai_center.item.updated': this.onAiCenterItemUpdated,
      'kanban_item.updated': this.onKanbanItemUpsert,
      'kanban_item.deleted': this.onKanbanItemDeleted,
      'integration.event_sent': this.onIntegrationEventSent,
      'kanban.item.created': this.onKanbanItemCreated,
      'kanban.item.updated': this.onKanbanItemUpdated,
      'kanban.item.deleted': this.onKanbanItemDeleted,
      'kanban.item.stage_changed': this.onKanbanItemStageChanged,
      'internal_chat.message.created': this.onInternalChatMessageCreated,
      'internal_chat.message.updated': this.onInternalChatMessageUpdated,
      'internal_chat.member.removed': this.onInternalChatMemberRemoved,
      'voice_call.incoming': this.onVoiceCallIncoming,
      'voice_call.outbound_connected': this.onVoiceCallOutboundConnected,
      'voice_call.outbound_accepted': this.onVoiceCallOutboundAccepted,
      'voice_call.ended': this.onVoiceCallEnded,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  onReconnect = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT);
  };

  // eslint-disable-next-line class-methods-use-this
  onDisconnected = () => {
    emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
  };

  isAValidEvent = data => {
    return this.app.$store.getters.getCurrentAccountId === data.account_id;
  };

  onMessageUpdated = data => {
    this.app.$store.dispatch('updateMessage', data);
  };

  onPresenceUpdate = data => {
    if (isImpersonating.value) return;
    this.app.$store.dispatch('contacts/updatePresence', data.contacts);
    this.app.$store.dispatch('agents/updatePresence', data.users);
    this.app.$store.dispatch('setCurrentUserAvailability', data.users);
  };

  onConversationContactChange = payload => {
    const { meta = {}, id: conversationId } = payload;
    const { sender } = meta || {};
    if (conversationId) {
      this.app.$store.dispatch('updateConversationContact', {
        conversationId,
        ...sender,
      });
    }
  };

  onAssigneeChanged = payload => {
    const { id } = payload;
    if (id) {
      this.app.$store.dispatch('updateConversation', payload);
    }
    this.throttledFetchConversationStats();
  };

  onConversationCreated = data => {
    this.app.$store.dispatch('addConversation', data);
    this.throttledFetchConversationStats();
  };

  onConversationRead = data => {
    this.app.$store.dispatch('updateConversation', data);
  };

  // eslint-disable-next-line class-methods-use-this
  onLogout = () => AuthAPI.logout();

  onMessageCreated = data => {
    const conversationId = data?.conversation_id;
    // Prefer nested conversation activity; fall back to message created_at so a
    // partial payload still reorders without throwing when conversation is missing.
    const lastActivityAt =
      data?.conversation?.last_activity_at ?? data?.created_at;
    DashboardAudioNotificationHelper.onNewMessage(data);
    this.app.$store.dispatch('addMessage', data);
    if (conversationId != null && lastActivityAt != null) {
      this.app.$store.dispatch('updateConversationLastActivity', {
        lastActivityAt,
        conversationId,
      });
    }
    // SiteUp: mensagem pública muda a filiação dos chips — a primeira resposta
    // do agente tira a conversa de `unattended` (Pendentes), e uma entrada nova
    // pode colocá-la lá. Sem isso o badge só era recalculado em troca de
    // filtro/status ou reload: a lista esvaziava e o número continuava lá.
    // Nota interna (private) não altera nenhum desses estados, então não conta.
    if (data?.private !== true) this.throttledFetchConversationStats();
    // SiteUP (D45): a sugestão da Central no chat some quando a equipe responde.
    emitter.emit(BUS_EVENTS.CONVERSATION_MESSAGE_CREATED, data);
  };

  // SiteUP (D45/D46): item da Central mudou numa conversa. A lista acende ou
  // apaga a faixa "Sugestão pronta" e o chat aberto tira ou recarrega o balão.
  onAiCenterItemUpdated = data => {
    if (!this.isAValidEvent(data)) return;
    if (data.conversation_id != null && data.ai_suggestion_pending != null) {
      this.app.$store.dispatch('setConversationAiSuggestion', {
        conversationId: data.conversation_id,
        pending: data.ai_suggestion_pending,
      });
    }
    emitter.emit(BUS_EVENTS.AI_CENTER_ITEM_UPDATED, data);
  };

  // eslint-disable-next-line class-methods-use-this
  onReload = () => window.location.reload();

  onStatusChange = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.throttledFetchConversationStats();
  };

  onConversationUpdated = data => {
    this.app.$store.dispatch('updateConversation', data);
    this.throttledFetchConversationStats();
  };

  onConversationUnreadCountChanged = () => {
    this.throttledFetchConversationUnreadCounts();
  };

  throttledFetchConversationUnreadCounts = () => {
    const now = Date.now();
    const elapsedTime = now - this.lastUnreadCountsFetchAt;

    if (
      this.lastUnreadCountsFetchAt === null ||
      elapsedTime >= UNREAD_COUNTS_REFETCH_THROTTLE_MS
    ) {
      this.clearUnreadCountsFetchTimer();
      this.fetchConversationUnreadCounts();
      return;
    }

    if (this.unreadCountsFetchTimer) return;

    this.unreadCountsFetchTimer = setTimeout(() => {
      this.unreadCountsFetchTimer = null;
      this.fetchConversationUnreadCounts();
    }, UNREAD_COUNTS_REFETCH_THROTTLE_MS - elapsedTime);
  };

  clearUnreadCountsFetchTimer = () => {
    if (!this.unreadCountsFetchTimer) return;

    clearTimeout(this.unreadCountsFetchTimer);
    this.unreadCountsFetchTimer = null;
  };

  fetchConversationUnreadCounts = () => {
    if (!this.isConversationUnreadCountsEnabled()) return;

    this.lastUnreadCountsFetchAt = Date.now();
    this.app.$store.dispatch('conversationUnreadCounts/get');
  };

  isConversationUnreadCountsEnabled = () => {
    const accountId = this.app.$store.getters.getCurrentAccountId;
    const isFeatureEnabled =
      this.app.$store.getters['accounts/isFeatureEnabledonAccount'];

    return isFeatureEnabled?.(
      accountId,
      FEATURE_FLAGS.CONVERSATION_UNREAD_COUNTS
    );
  };

  onTypingOn = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/create', {
      conversationId,
      user,
    });
    this.initTimer({ conversation, user });
  };

  onTypingOff = ({ conversation, user }) => {
    const conversationId = conversation.id;

    this.clearTimer(conversationId);
    this.app.$store.dispatch('conversationTypingStatus/destroy', {
      conversationId,
      user,
    });
  };

  onConversationMentioned = data => {
    this.app.$store.dispatch('addMentions', data);
  };

  clearTimer = conversationId => {
    const timerEvent = this.CancelTyping[conversationId];

    if (timerEvent) {
      clearTimeout(timerEvent);
      this.CancelTyping[conversationId] = null;
    }
  };

  initTimer = ({ conversation, user }) => {
    const conversationId = conversation.id;
    // Turn off typing automatically after 30 seconds
    this.CancelTyping[conversationId] = setTimeout(() => {
      this.onTypingOff({ conversation, user });
    }, 30000);
  };

  // eslint-disable-next-line class-methods-use-this
  fetchConversationStats = () => {
    emitter.emit('fetch_conversation_stats');
  };

  // Mesma forma do throttle de unread (leading + trailing): a primeira mensagem
  // reconta na hora, e uma rajada agenda UMA recontagem no fim da janela em vez
  // de uma por mensagem. `fetchStatusCounts` no ChatList ainda tem debounce
  // próprio de 800ms depois disto.
  throttledFetchConversationStats = () => {
    const now = Date.now();
    const elapsedTime = now - this.lastStatsFetchAt;

    if (
      this.lastStatsFetchAt === null ||
      elapsedTime >= STATS_REFETCH_THROTTLE_MS
    ) {
      this.clearStatsFetchTimer();
      this.lastStatsFetchAt = now;
      this.fetchConversationStats();
      return;
    }

    if (this.statsFetchTimer) return;

    this.statsFetchTimer = setTimeout(() => {
      this.statsFetchTimer = null;
      this.lastStatsFetchAt = Date.now();
      this.fetchConversationStats();
    }, STATS_REFETCH_THROTTLE_MS - elapsedTime);
  };

  clearStatsFetchTimer = () => {
    if (!this.statsFetchTimer) return;

    clearTimeout(this.statsFetchTimer);
    this.statsFetchTimer = null;
  };

  onContactDelete = data => {
    this.app.$store.dispatch(
      'contacts/deleteContactThroughConversations',
      data.id
    );
    this.fetchConversationStats();
  };

  onContactUpdate = data => {
    this.app.$store.dispatch('contacts/updateContact', data);
  };

  onNotificationCreated = data => {
    this.app.$store.dispatch('notifications/addNotification', data);

    // Phase 2.8: rich toast estilo WhatsApp (avatar + nome + body + cor por tipo).
    // Substitui o toast texto-plano da 2.7. Dedup via `tag` (notification_type + id).
    const notif = data && data.notification;
    if (notif) {
      // Resolve avatar + nome do remetente. Caminhos:
      // - secondary_actor.sender (Chatwoot nativo — mensagem com sender Contact ou User)
      // - primary_actor (Conversation.meta.sender ou Contact direto)
      const sender =
        notif.secondary_actor?.sender ||
        notif.primary_actor?.meta?.sender ||
        (notif.primary_actor_type === 'Contact' ? notif.primary_actor : null);
      const avatar = sender?.thumbnail || sender?.avatar_url || null;
      const senderName = sender?.name || sender?.available_name || '';

      // Phase 2.8 fix: Chatwoot tem bug conhecido onde push_message_title recebe o mesmo
      // valor de push_message_body (ver Notification#primary_actor_data — tem TODO la).
      // Pra VOIP usamos meta.title/body (nao sofrem do bug); pra Chatwoot nativo
      // reconstruimos um title semantico a partir do notification_type + sender,
      // e removemos o prefixo "Nome:" do body pra nao repetir.
      let title;
      let body;
      if (notif.meta?.title) {
        // VOIP: meta direto
        title = notif.meta.title;
        body = notif.meta.body || '';
      } else {
        // Nativo: reconstroi
        const rawBody =
          notif.push_message_body || notif.push_message_title || '';
        // Remove prefixo "Nome do remetente: " se existir (builder do Chatwoot prefixa)
        const prefixMatch =
          senderName && rawBody.startsWith(`${senderName}:`)
            ? rawBody.slice(senderName.length + 1).trim()
            : rawBody;
        body = prefixMatch;
        // Title = nome do sender (se temos) ou label do tipo de evento
        title =
          senderName ||
          TYPE_TITLE_FALLBACK[notif.notification_type] ||
          'Nova notificação';
      }

      // Constroi URL pra navegar ao clicar na notif:
      // 1) meta.url (VOIP usa isso)
      // 2) Se primary_actor é Conversation, monta /app/accounts/:aid/conversations/:display_id
      // 3) Se Contact, monta /app/accounts/:aid/contacts/:id
      let url = notif.meta?.url || null;
      const accountId = notif.account_id;
      const pa = notif.primary_actor;
      if (!url && pa) {
        if (notif.primary_actor_type === 'Conversation' && pa.id) {
          const displayId = pa.display_id || pa.id;
          url = `/app/accounts/${accountId}/conversations/${displayId}`;
        } else if (notif.primary_actor_type === 'Contact' && pa.id) {
          url = `/app/accounts/${accountId}/contacts/${pa.id}`;
        }
      }

      emitter.emit('voipRichToast', {
        id: notif.id,
        type: notif.notification_type,
        title,
        body,
        url,
        avatar,
        senderName,
        created_at: notif.created_at
          ? new Date(notif.created_at * 1000).toISOString()
          : new Date().toISOString(),
        tag: `${notif.notification_type}_${notif.id}`,
      });

      // Som opcional
      try {
        if (typeof DashboardAudioNotificationHelper.play === 'function') {
          DashboardAudioNotificationHelper.play();
        }
      } catch (_e) {
        /* sem som, ok */
      }
    }
  };

  onNotificationDeleted = data => {
    this.app.$store.dispatch('notifications/deleteNotification', data);
  };

  onNotificationUpdated = data => {
    this.app.$store.dispatch('notifications/updateNotification', data);
  };

  onCopilotMessageCreated = data => {
    this.app.$store.dispatch('copilotMessages/upsert', data);
  };

  // #53: indicador "IA processando" ao vivo. Reemite via bus; o componente no
  // thread mostra logo girando + tempo + tools (status started/tool/done).
  onCaptainProcessing = data => {
    if (!this.isAValidEvent(data)) return;
    emitter.emit('captainProcessing', data);
  };

  onInternalChatMessageCreated = data => {
    if (!this.isAValidEvent(data)) return;
    emitter.emit(BUS_EVENTS.INTERNAL_CHAT_MESSAGE_CREATED, data);
  };

  onInternalChatMemberRemoved = data => {
    if (!this.isAValidEvent(data)) return;
    emitter.emit(BUS_EVENTS.INTERNAL_CHAT_MEMBER_REMOVED, data);
  };

  onInternalChatMessageUpdated = data => {
    if (!this.isAValidEvent(data)) return;
    emitter.emit(BUS_EVENTS.INTERNAL_CHAT_MESSAGE_UPDATED, data);
  };

  onEnrichmentCompleted = () => {
    this.app.$store.dispatch('accounts/get', { silent: true });
  };

  onCacheInvalidate = data => {
    const keys = data.cache_keys;
    this.app.$store.dispatch('labels/revalidate', { newKey: keys.label });
    this.app.$store.dispatch('inboxes/revalidate', { newKey: keys.inbox });
    this.app.$store.dispatch('teams/revalidate', { newKey: keys.team });
  };

  onKanbanItemAccessChanged = data => {
    if (!this.isAValidEvent(data)) return;
    emitter.emit(BUS_EVENTS.KANBAN_ITEM_ACCESS_CHANGED, data);
  };

  onGoogleCalendarAccessChanged = data => {
    const ids = [data?.account_id, data?.connection_id, data?.user_id];
    if (!ids.every(id => Number.isSafeInteger(id) && id > 0)) return;
    if (!this.isAValidEvent(data)) return;
    const currentUserId = this.app.$store.getters.getCurrentUserID;
    if (data.user_id !== currentUserId) return;
    emitter.emit(BUS_EVENTS.GOOGLE_CALENDAR_ACCESS_CHANGED, {
      account_id: data.account_id,
      connection_id: data.connection_id,
      user_id: data.user_id,
    });
  };

  onKanbanCardChanged = data => {
    if (!this.isAValidEvent(data)) return;
    emitter.emit(BUS_EVENTS.KANBAN_ITEM_CHANGED, {
      account_id: data.account_id,
      id: data.id,
    });
  };

  onKanbanCardsInvalidated = data => {
    if (!this.isAValidEvent(data)) return;
    emitter.emit(BUS_EVENTS.KANBAN_ITEM_CHANGED, {
      account_id: data.account_id,
      all: true,
    });
  };

  onKanbanItemUpsert = data => {
    if (this.isAValidEvent(data)) {
      emitter.emit(BUS_EVENTS.KANBAN_ITEM_CHANGED, {
        account_id: data.account_id,
        id: data.id,
      });
    }
    this.app.$store.dispatch('kanban/applyRealtimeUpsert', data);
    // The conversation side panel reads `currentChat.kanban_items` (loaded
    // once at fetch time), so we also patch the conversation in the
    // conversations store when the item is linked to a conversation.
    if (data.conversation_display_id) {
      this.app.$store.dispatch('updateConversationKanbanItems', {
        conversationId: data.conversation_display_id,
        kanbanItems: [data],
      });
    }
  };

  onKanbanItemDeleted = data => {
    const id = data?.id || data?.kanban_item_id;
    if (id && this.isAValidEvent(data)) {
      emitter.emit(BUS_EVENTS.KANBAN_ITEM_CHANGED, {
        account_id: data.account_id,
        id,
      });
    }
    if (id) this.app.$store.dispatch('kanban/applyRealtimeDelete', Number(id));
    if (data?.conversation_display_id) {
      this.app.$store.dispatch('updateConversationKanbanItems', {
        conversationId: data.conversation_display_id,
        kanbanItems: [],
      });
    }
    if (id) emitter.emit('kanbanItemDeleted', { itemId: Number(id) });
  };

  // Mostra toast quando uma integracao externa (Google Ads / Meta CAPI) foi
  // disparada por mudanca de etapa no kanban. Filtra por agente que disparou
  // pra evitar ruido pros outros (a timeline do card persiste pra todos).
  onIntegrationEventSent = data => {
    const currentAccountId = this.app.$store.getters.getCurrentAccountId;
    if (data.account_id && data.account_id !== currentAccountId) return;

    const currentUserId = this.app.$store.getters.getCurrentUserID;
    const triggeredBy = data.triggered_by_user_id;
    // Mostra pro agente que moveu, ou pra todos quando origem e API/sistema
    if (triggeredBy && triggeredBy !== currentUserId) return;

    const providerLabel =
      {
        google_ads: 'Google Ads',
        meta_capi: 'Meta Ads',
      }[data.provider] || data.provider;

    const stage = data.event_label || data.stage_key || 'etapa';

    let message;
    switch (data.status) {
      case 'success':
        message = `✓ Conversao enviada ao ${providerLabel} — ${stage}`;
        break;
      case 'partial_failure':
        message = `⚠ ${providerLabel}: enviado com ressalvas (${stage})`;
        break;
      case 'error':
        message = `✖ Falha no ${providerLabel} (${stage})${data.error ? `: ${String(data.error).slice(0, 80)}` : ''}`;
        break;
      default:
        return;
    }

    emitter.emit('newToastMessage', { message, action: null });
  };

  onKanbanItemCreated = data => {
    const createdItem = data?.kanban_item;
    if (!createdItem) return;

    // O backend emite 'kanban.item.*' (com pontos) — antes isto só emitia o mitt
    // pro board e deixava o store do board E o painel da conversa
    // (currentChat.kanban_items) sem o card recém-criado até um F5. Passamos pelo
    // upsert completo (store do kanban + patch da conversa) e seguimos emitindo o
    // mitt que o KanbanColumn/KanbanBoard escutam.
    this.onKanbanItemUpsert(createdItem);
    emitter.emit('kanbanItemCreated', createdItem);
  };

  onKanbanItemUpdated = data => {
    const updatedItem = data?.kanban_item;
    if (!updatedItem) return;

    this.onKanbanItemUpsert(updatedItem);
    emitter.emit('kanbanItemUpdated', updatedItem);
  };

  onKanbanItemStageChanged = data => {
    const movedItem = data?.kanban_item;
    if (!movedItem) return;

    // Mantém store do board + painel da conversa em sincronia (mesma razão do
    // onKanbanItemCreated/Updated) antes de notificar os componentes via mitt.
    this.onKanbanItemUpsert(movedItem);

    const itemId = Number(movedItem.id);
    const fromStage = data?.from_stage;
    const toStage = data?.to_stage || movedItem.funnel_stage;

    emitter.emit('kanbanItemMoved', {
      itemId,
      fromStage,
      toStage,
      itemData: movedItem,
    });

    emitter.emit('kanbanItemMovedBetweenStages', {
      itemId,
      fromStage,
      toStage,
      funnelId: movedItem.funnel_id,
    });

    emitter.emit('kanbanItemUpdated', movedItem);
  };

  onVoiceCallIncoming = data => {
    if (data?.provider !== VOICE_CALL_PROVIDERS.WHATSAPP) return;
    // Defense in depth: the server already filters to online agent streams,
    // but if anything ever broadcasts to a broader stream (e.g. account-wide),
    // an agent who's set availability=offline/busy shouldn't ring.
    const availability = this.app.$store.getters.getCurrentUserAvailability;
    if (availability !== 'online') return;

    useCallsStore().addCall({
      callSid: data.call_id,
      callId: data.id,
      conversationId: data.conversation_id,
      inboxId: data.inbox_id,
      callDirection: VOICE_CALL_DIRECTION.INBOUND,
      provider: VOICE_CALL_PROVIDERS.WHATSAPP,
      sdpOffer: data.sdp_offer,
      iceServers: data.ice_servers,
      caller: data.caller,
    });
  };

  // `connect` is the WebRTC tunnel-ready signal (fires ~20s before pickup
  // for outbound). Apply the SDP answer so the handshake completes during
  // ringing, but stay non-active until `outbound_accepted` arrives.
  // eslint-disable-next-line class-methods-use-this
  onVoiceCallOutboundConnected = async data => {
    if (data?.provider !== VOICE_CALL_PROVIDERS.WHATSAPP || !data.sdp_answer)
      return;
    // Account-wide broadcast that can arrive before /initiate sets this tab's
    // call id. applyOutboundAnswer filters foreign calls and buffers the answer
    // until the id is known, so we must not drop it here on a null activeCallId.
    try {
      await applyOutboundAnswer(data.id, data.sdp_answer);
    } catch (_) {
      /* noop */
    }
  };

  // Real pickup signal — Meta sends status=ACCEPTED on the call when the
  // contact answers. Flip active (timer starts) and arm the recorder.
  // eslint-disable-next-line class-methods-use-this
  onVoiceCallOutboundAccepted = data => {
    if (data?.provider !== VOICE_CALL_PROVIDERS.WHATSAPP) return;
    const store = useCallsStore();
    if (!store.calls.some(c => c.callSid === data.call_id)) return;
    store.setCallActive(data.call_id);
    armOutboundRecorder();
  };

  // eslint-disable-next-line class-methods-use-this
  onVoiceCallEnded = async data => {
    if (data?.provider !== VOICE_CALL_PROVIDERS.WHATSAPP) return;
    // The store entry should always be removed for this account-wide broadcast,
    // but the WebRTC/recorder teardown must only run for the call this tab owns
    // — otherwise an unrelated agent's call ending would stop this tab's
    // recorder and upload its chunks against the wrong call id.
    if (isLocalWhatsappCall(data.id)) {
      // Await upload before removeCall — the store's sync teardown would otherwise
      // wipe the recorder chunks before they reach the server.
      try {
        await handleWhatsappRemoteEnd(data.id);
      } catch (_) {
        /* noop */
      }
    }
    useCallsStore().removeCall(data.call_id);
  };
}

export default {
  init(store, pubsubToken) {
    return new ActionCableConnector({ $store: store }, pubsubToken);
  },
};
